import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import TopHospitalsTurkey from '../components/TopHospitalsTurkey';
import StarDoctorsTurkey from '../components/StarDoctorsTurkey';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import BlogsCarousel from '../components/BlogsCarousel';
import { Link } from 'react-router-dom';
import { getSpecialitiesForCountry } from '../utils/hospitalUtils';
import api from '../services/api';

const BestHospitalTurkey = () => {
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('');

  useEffect(() => {
    const fetchSpecialities = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the same API service that other components use successfully
        try {
          const response = await api.get('/hospitals', { 
            params: { limit: 100 } 
          });
          
          let hospitalsData = [];
          
          // Handle API response format
          if (response.data.hospitals && Array.isArray(response.data.hospitals)) {
            hospitalsData = response.data.hospitals;
          } else if (Array.isArray(response.data)) {
            hospitalsData = response.data;
          }
          
          // Filter by country (Turkey) on frontend
          const turkeyHospitals = hospitalsData.filter(hospital => 
            hospital.country && hospital.country.toLowerCase() === 'turkey'
          );
          
          // Extract specialities from Turkey hospitals data
          const specialitiesSet = new Set();
          turkeyHospitals.forEach(hospital => {
            if (hospital.specialities && Array.isArray(hospital.specialities)) {
              hospital.specialities.forEach(spec => {
                if (spec && spec.trim()) {
                  specialitiesSet.add(spec.trim());
                }
              });
            }
          });
          
          const extractedSpecialities = Array.from(specialitiesSet).sort();
          
          if (extractedSpecialities.length > 0) {
            setSpecialties(extractedSpecialities);
            setDataSource(`API (Live Database - ${turkeyHospitals.length} Turkey hospitals)`);
            setError(null);
          } else {
            throw new Error('No specialities found in Turkey hospital data');
          }
          
        } catch (apiError) {
          console.warn('API call failed, using local data:', apiError.message);
          // Use local data as fallback
          const localSpecialities = getSpecialitiesForCountry('Turkey');
          if (localSpecialities.length > 0) {
            setSpecialties(localSpecialities);
            setDataSource('Local Data');
            setError('API temporarily unavailable, showing data from local cache');
          } else {
            setSpecialties([]);
            setDataSource('No Data');
            setError('Unable to load hospital specialities');
          }
        }
        
      } catch (err) {
        console.error('Error in fetchSpecialities:', err);
        setError('Unable to load hospital data');
        setDataSource('Error');
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center">Best Hospitals in Turkey</h1>
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1976f2]"></div>
              <p className="mt-4 text-gray-600">Loading specialities...</p>
            </div>
          </div>
        </main>
        <Footer />
      <FloatingChatButton />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center">Best Hospitals in Turkey</h1>
        
        {error && (
          <div className="max-w-4xl mx-auto mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              <strong>Notice:</strong> {error}
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {specialties.map((label, idx) => (
            <Link
              key={idx}
              to={`/besthospitaldetails?speciality=${encodeURIComponent(label)}&country=Turkey`}
              className="flex items-center justify-center border-2 border-[#1976f2] rounded-2xl p-4 text-base font-semibold text-blue-900 bg-white text-center transition hover:border-blue-800 hover:bg-blue-50"
            >
              Best {label} Hospitals in Turkey
            </Link>
          ))}
        </div>
        
        {/* Intro Paragraph for Top Hospitals */}
        <div className="max-w-4xl mx-auto mt-12 mb-8 text-center text-gray-600 text-base md:text-lg font-medium">
          Looking for the best hospitals in Turkey? Whether you need advanced heart care, oncology treatment, neurosurgery, or joint replacement, we help you
          find top-rated hospitals across every medical specialty. Our curated list includes the best hospitals for cancer care, gynecology, pediatrics, urology,
          gastroenterology, and more. Compare hospitals, read reviews, and make informed healthcare decisions with ease. Discover trusted medical
          facilities offering world-class care with Turkey's leading healthcare professionals.
        </div>
      </main>
      
      <TopHospitalsTurkey />
      <StarDoctorsTurkey />
      <TestimonialVideoCarousel />
      <BlogsCarousel />
      <Footer />
    </div>
  );
};

export default BestHospitalTurkey; 